import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useRouter } from '../RouterContext';

const LinkedinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
    <circle cx="4" cy="4" r="2"/>
  </svg>
);

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { navigate } = useRouter();

  return (
    <footer style={styles.footer}>
      <div style={styles.top}>
        <div style={styles.container}>
          {/* Brand */}
          <div style={styles.brand}>
            <div style={styles.logo}>
              <span style={styles.logoMark}>2GF</span>
              <span style={styles.logoText}>Innovation Systems</span>
            </div>
            <p style={styles.brandDesc}>
              Soluções tecnológicas inovadoras para indústrias inteligentes,
              transformação digital e cidades do futuro.
            </p>
            <div style={styles.socials}>
              <a
                href="https://linkedin.com/company/2gf-innovation-systems"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.socialIcon}
                aria-label="LinkedIn"
              >
                <LinkedinIcon />
              </a>
              <a href="mailto:hello@2gfinnovationsystems.pt" style={styles.socialIcon} aria-label="Email">
                <Mail size={18} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div style={styles.col}>
            <h4 style={styles.colTitle}>Serviços</h4>
            <ul style={styles.colList}>
              {['Smart Industry', 'Transformação Digital', 'Smart Cities', 'IoT & Sensores', 'Big Data Analytics', 'IT Outsourcing'].map(s => (
                <li key={s}><button style={styles.colLink} onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}>{s}</button></li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div style={styles.col}>
            <h4 style={styles.colTitle}>Empresa</h4>
            <ul style={styles.colList}>
              {[
                { label: 'Sobre Nós', href: '#about' },
                { label: 'Serviços', href: '#services' },
                { label: 'Contacto', href: '#contact' },
              ].map(l => (
                <li key={l.href}>
                  <button style={styles.colLink} onClick={() => document.querySelector(l.href)?.scrollIntoView({ behavior: 'smooth' })}>
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div style={styles.col}>
            <h4 style={styles.colTitle}>Contacto</h4>
            <ul style={styles.colList}>
              <li style={styles.contactItem}>
                <Mail size={14} color="#8A99AF" />
                <a href="mailto:hello@2gfinnovationsystems.pt" style={styles.contactLink}>hello@2gfinnovationsystems.pt</a>
              </li>
              <li style={styles.contactItem}>
                <Phone size={14} color="#8A99AF" />
                <a href="tel:+351969059898" style={styles.contactLink}>+351 969 059 898</a>
              </li>
              <li style={{ ...styles.contactItem, alignItems: 'flex-start' }}>
                <MapPin size={14} color="#8A99AF" style={{ marginTop: '3px', flexShrink: 0 }} />
                <span style={styles.contactLink}>Centro de Inovação Empresarial da Covilhã, 6200-053 Covilhã</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* PRR / EU Funding bar */}
      <div style={styles.prr}>
        <div style={styles.prrInner}>
          <div style={styles.prrBlock}>
            <a href="https://2gfinnovationsystems.pt/Cartaz%202GF%20revisto.pdf" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <img
                src="https://2gfinnovationsystems.pt/assets/img/prr.png"
                alt="PRR · República Portuguesa · Financiado pela União Europeia NextGenerationEU"
                style={styles.prrImg}
              />
            </a>
            <a href="https://recuperarportugal.gov.pt" target="_blank" rel="noopener noreferrer" style={styles.prrUrl}>
              recuperarportugal.gov.pt
            </a>
          </div>
        </div>
      </div>

      <div style={styles.bottom}>
        <div style={styles.bottomInner}>
          <span style={styles.copyright}>
            © {currentYear} 2GF Innovation Systems · NIF 516285858
          </span>
          <div style={styles.legal}>
            <button style={styles.legalLink} onClick={() => navigate('privacy')}>Política de Privacidade</button>
            <span style={styles.legalDot}>·</span>
            <button style={styles.legalLink} onClick={() => navigate('cookies')}>Política de Cookies</button>
            <span style={styles.legalDot}>·</span>
            <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noopener noreferrer" style={{ ...styles.legalLink, textDecoration: 'none' }}>Livro de Reclamações</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  footer: {
    background: '#020F1E',
  },
  top: {
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '5rem 0 4rem',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
    gap: '3rem',
  },
  brand: {},
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1.25rem',
  },
  logoMark: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#0057FF',
    letterSpacing: '-0.02em',
  },
  logoText: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.7)',
  },
  brandDesc: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.35)',
    lineHeight: 1.75,
    marginBottom: '1.5rem',
  },
  socials: {
    display: 'flex',
    gap: '0.75rem',
  },
  socialIcon: {
    width: '38px',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.5)',
    transition: 'all 0.2s',
    textDecoration: 'none',
  },
  col: {},
  colTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: '1.25rem',
  },
  colList: {
    listStyle: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  colLink: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.4)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    transition: 'color 0.2s',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
  },
  contactLink: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.4)',
    textDecoration: 'none',
    lineHeight: 1.5,
  },
  prr: {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    padding: '1.25rem 0',
    background: 'rgba(255,255,255,0.02)',
  },
  prrInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
  },
  prrBlock: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  prrImg: {
    height: '60px',
    objectFit: 'contain',
  },
  prrUrl: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    color: '#00C896',
    textDecoration: 'underline',
  },
  bottom: {
    padding: '1.5rem 0',
  },
  bottomInner: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  copyright: {
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.25)',
  },
  legal: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  legalLink: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.25)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'color 0.2s',
  },
  legalDot: {
    color: 'rgba(255,255,255,0.15)',
    fontSize: '0.8rem',
  },
};

export default Footer;
